/**
 * usePublicAction — running an action button on a public record.
 *
 * Everything a page needs to turn a click into a write and report what
 * happened: the session check, the sign-in detour and the return from it, the
 * duplicate-click guard, the server's own account of whether the row was saved,
 * and the set of buttons this visitor has already completed.
 *
 * Lifted out of PublicRecordDetailPage so the React event detail page runs the
 * same machinery rather than a second implementation of it. The rules here are
 * the ones a second implementation would get wrong quietly: confirming a write
 * the server did not confirm, or losing a visitor's answers at the sign-in
 * detour, both look fine in a happy-path test.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getPublicAccessKey } from './usePublicTableView';
import {
  buildSubmitUrl, resumeButtonFrom, buildSignInUrl,
  stashPendingAction, takePendingAction, confirmsWrite,
  completedKeysForTable, withCompleted, completedActionsFrom,
} from '../utils/actionRunner';
import { isUserLoggedIn } from '../utils/utils';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';

// The public API allows a burst of five writes a second. Seats are written one
// at a time, so they are spaced just outside that rather than racing it, and a
// throttle that still happens is waited out rather than shown to anyone.
const WRITE_GAP_MS = 260;
const THROTTLE_RETRIES = 3;

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const actionApi = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

export default function usePublicAction({ tableSlug, recordId, actionButtons }) {
  // Whether a visitor is signed in decides whether an action submits or detours
  // through sign-in. Checked once per page rather than per click, so the button
  // does not stall on a network round trip.
  const [signedIn, setSignedIn] = useState(null);
  const [actionState, setActionState] = useState(null);

  // Buttons this visitor has already completed on THIS record, as
  // `<recordId>/<buttonId>`. Empty for a signed-out visitor, for a server that
  // does not send the field, and for any failure — all of which draw the button
  // exactly as before.
  const [completedKeys, setCompletedKeys] = useState(new Set());

  useEffect(() => {
    let alive = true;
    // One call, two answers: whether they are signed in, and what they have
    // already done.
    isUserLoggedIn().then(u => {
      if (!alive) return;
      setSignedIn(Boolean(u));
      setCompletedKeys(completedKeysForTable(
        completedActionsFrom(u), envConfig.organizationId, envConfig.applicationId, tableSlug,
      ));
    });
    return () => { alive = false; };
  }, [tableSlug]);

  // Guards against a second submission of the same click. A React state flag is
  // not enough: setActionState is asynchronous, so two clicks landing in the
  // same tick both read the old state and both post. A ref changes
  // synchronously, which is the only thing that holds between them.
  const inFlightRef = useRef(false);

  const submitAction = useCallback(async ({ button, answers, payload }) => {
    // Send an anonymous visitor to sign in FIRST, rather than posting and
    // reading the refusal. APIHelper throws on 401 instead of returning it, so
    // a status check after the fact never runs — and the visitor would be left
    // with a generic failure and no way forward.
    //
    // Resolve the session BEFORE posting. On a fresh page load the background
    // check may not have finished, leaving this null — and posting on a null
    // would hit a 401 that APIHelper turns into its own sign-in redirect,
    // discarding the resume marker and the answers with it.
    let authed = signedIn;
    if (authed === null || authed === undefined) {
      // Signed in HERE means "is an application user of this application", not
      // merely "has some Django session". Someone signed into the admin in
      // another tab has a session cookie this origin sends, and login/check
      // answers 200 for them — with no user_id, because they are not a member
      // of this application.
      const who = await isUserLoggedIn();
      authed = Boolean(who && who.user_id);
      setSignedIn(authed);
    }

    if (!authed) {
      // Keep what they typed, so they come back to a filled form rather than
      // an empty one — otherwise the sign-in detour reads as a failed click.
      stashPendingAction(button.id, answers, payload);
      window.location.href = buildSignInUrl(tableSlug, recordId, button.id);
      return;
    }

    if (inFlightRef.current) return;
    inFlightRef.current = true;

    setActionState({ status: 'busy', id: button.id });
    try {
      const resp = await actionApi.apiCall(
        buildSubmitUrl({
          orgId: envConfig.organizationId, appId: envConfig.applicationId,
          tableSlug, recordId, buttonId: button.id,
        }),
        { method: 'POST', credentials: 'include',
          // The public access key is required on EVERY public endpoint, writes
          // included. Without it the server answers 404, which reads as "no
          // such record" and hides the real cause.
          headers: { 'Content-Type': 'application/json',
                     'X-Public-Access-Key': await getPublicAccessKey(tableSlug) },
          // Only a button id (in the path), the visitor's answers and the
          // template's hidden values. No table, no column, no record id — the
          // server decides all of that from the button's own configuration.
          body: JSON.stringify({ answers: answers || {}, payload: payload || {} }) },
      );
      const body = await resp.json().catch(() => ({}));

      // A signed-out visitor must be sent to sign in and brought back, not left
      // staring at a failure. The form is rendered inline, so this is the first
      // point at which we learn there is no session.
      if (resp.status === 401) {
        window.location.href = buildSignInUrl(tableSlug, recordId, button.id);
        return;
      }

      if (!resp.ok) {
        // Released on failure so the visitor can correct the form and try
        // again. Only a SUCCESSFUL write keeps the guard closed — that is the
        // one case where a second attempt would be a duplicate.
        inFlightRef.current = false;
        setActionState({ status: 'error', id: button.id,
                         message: body.error || 'Could not complete that.',
                         detail: body.detail });
        return;
      }
      // A 2xx is not proof the record was saved. Confirming a write we cannot
      // substantiate is the worst outcome available here — the visitor leaves
      // believing they registered. Only the server's own account of the write
      // earns a confirmation.
      if (!confirmsWrite(body)) {
        inFlightRef.current = false;
        setActionState({ status: 'error', id: button.id,
                         message: body.error || 'That did not save. Please try again.',
                         detail: body.detail });
        return;
      }

      // `reveal` carries the values that only existed once the write happened —
      // a pass code, and a token as a picture. Absent for every button that has
      // not configured one.
      setActionState({ status: 'done', id: button.id,
                       message: body.message || 'Done.',
                       reveal: body.reveal || null });

      // The server has noted this in the session; mirroring it here means the
      // button is withdrawn now rather than on the next load.
      setCompletedKeys(keys => withCompleted(keys, recordId, button.id));
    } catch (e) {
      inFlightRef.current = false;

      // The server refused because nobody is signed in. APIHelper throws rather
      // than returning, so this is the only place the refusal can be caught —
      // the resp.status check above never sees it.
      if (e && (e.type === 'requires_auth'
                || (e.detail && e.detail.requires_auth === true))) {
        setSignedIn(false);
        stashPendingAction(button.id, answers, payload);
        window.location.href = buildSignInUrl(tableSlug, recordId, button.id);
        return;
      }

      setActionState({ status: 'error', id: button.id, message: 'Could not complete that.' });
    }
  }, [tableSlug, recordId, signedIn]);


  /**
   * Posts ONE action and reports what happened, without touching page state.
   *
   * Added for the entry modal, which registers a person for several activities
   * in one go: it runs this once per chosen activity and shows a line per
   * result, so a refusal on the second does not erase the success of the first.
   * `submitAction` below is the single-shot form of the same call and keeps the
   * page-level state it always did.
   *
   * The returned `message` is always the SERVER's own sentence when it sent one
   * — "This event is full", "You have already registered" — because that is the
   * only text that tells a visitor what actually went wrong. Never a status
   * code, never an HTML body.
   */
  const runAction = useCallback(async ({ button, answers, payload }) => {
    try {
      const resp = await actionApi.apiCall(
        buildSubmitUrl({
          orgId: envConfig.organizationId, appId: envConfig.applicationId,
          tableSlug, recordId, buttonId: button.id,
        }),
        { method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json',
                     'X-Public-Access-Key': await getPublicAccessKey(tableSlug) },
          body: JSON.stringify({ answers: answers || {}, payload: payload || {} }) },
      );

      // A body that is not JSON is a server error page, a proxy notice or a
      // gateway timeout. Its TEXT must never reach the visitor — that is how a
      // screen ends up showing raw HTML — so it becomes a plain sentence and
      // the status code is kept for whoever reads the report.
      const body = await resp.json().catch(() => null);

      if (resp.status === 401) {
        return { ok: false, requiresAuth: true,
                 message: 'Please sign in to complete this entry.' };
      }

      if (!body) {
        return { ok: false, status: resp.status,
                 message: `The server could not complete this (error ${resp.status}). Please try again.` };
      }

      // Throttled, not refused. The public API allows a burst of five writes a
      // second, and a person taking ten seats sends ten — so this is an
      // ordinary, expected answer that the caller waits out, never something to
      // show a visitor as a failure.
      if (resp.status === 429) {
        const header = Number(resp.headers?.get?.('Retry-After'));
        const said = /available in (\d+)/i.exec(
          typeof body.detail === 'string' ? body.detail : '');
        return { ok: false, throttled: true,
                 retryAfter: header || (said ? Number(said[1]) : 1),
                 message: 'The server is busy. Retrying…' };
      }

      if (!resp.ok) {
        return { ok: false, status: resp.status,
                 message: body.error || 'Could not complete that.',
                 detail: body.detail };
      }

      // A 2xx is not proof of a write. Only the server's own account of it
      // earns a confirmation — see confirmsWrite.
      if (!confirmsWrite(body)) {
        return { ok: false, status: resp.status,
                 message: body.error || 'That did not save. Please try again.',
                 detail: body.detail };
      }

      return { ok: true, message: body.message || 'Done.', reveal: body.reveal || null };
    } catch (e) {
      if (e && (e.type === 'requires_auth'
                || (e.detail && e.detail.requires_auth === true))) {
        return { ok: false, requiresAuth: true,
                 message: 'Please sign in to complete this entry.' };
      }
      // A network failure, not a refusal. Said as such, rather than as a
      // generic "something went wrong" that sends someone hunting the server.
      return { ok: false,
               message: e?.message
                 ? `Could not reach the server: ${e.message}`
                 : 'Could not reach the server. Check your connection and try again.' };
    }
  }, [tableSlug, recordId]);

  /**
   * Registers for SEVERAL SEATS, one write each, in order.
   *
   * Sequential rather than parallel: each write is checked against the event's
   * capacity and against what this person already holds, and firing them
   * together would race both. It also keeps the report readable — the results
   * come back in the order the visitor ticked them.
   *
   * One failure does not stop the rest. Entering the 10K should not be lost
   * because the yoga filled up while the visitor was choosing.
   *
   * Each item carries its own payload, which is where the checkout id and the
   * seat number ride. Those two are what let the database refuse a REPEAT of a
   * seat while still accepting a genuine second one — see the unique rule on
   * (checkout_id, seat_no). Without them every seat looks like every other.
   */
  const runActionForEach = useCallback(async ({ button, answers, payload, items }) => {
    const results = [];
    let last = 0;

    for (const item of items) {
      const body = { ...(payload || {}), ...(item.payload || {}) };

      // Paced under the server's burst allowance rather than sprinting into it.
      // Ten seats at full speed trip a five-a-second limit on the sixth, and
      // the visitor is told their entry failed when nothing was wrong with it.
      const since = Date.now() - last;
      // eslint-disable-next-line no-await-in-loop
      if (since < WRITE_GAP_MS) await wait(WRITE_GAP_MS - since);

      let result;
      for (let attempt = 0; attempt <= THROTTLE_RETRIES; attempt += 1) {
        last = Date.now();
        // eslint-disable-next-line no-await-in-loop
        result = await runAction({ button, answers, payload: body });
        if (!result.throttled) break;
        // The server says how long to wait; it is believed, with a floor so a
        // zero can never turn this into a spin.
        // eslint-disable-next-line no-await-in-loop
        await wait(Math.max(1, result.retryAfter || 1) * 1000);
      }

      // A throttle that survived every retry is reported honestly rather than
      // as a refusal — the entry did not happen, and the reason was us.
      if (result.throttled) {
        result = { ok: false,
                   message: 'The server was too busy to take this entry. Please try it again.' };
      }

      results.push({ ...item, result });
      if (result.ok) setCompletedKeys(keys => withCompleted(keys, recordId, button.id));
    }
    return results;
  }, [runAction, recordId]);

  // A sign-in round trip names the button to resume, so the visitor does not
  // have to click twice. Guarded by a ref: this must fire once, not on every
  // re-render the submit itself causes.
  const resumedRef = useRef(false);
  const resumeAfterSignIn = useCallback(() => {
    if (resumedRef.current || !signedIn) return;

    const resumeId = resumeButtonFrom(window.location.search, actionButtons);
    if (!resumeId) return;

    resumedRef.current = true;
    const pending = takePendingAction(resumeId);
    // Nothing stashed (a hand-typed link, or a new tab) — leave the form for
    // them to fill rather than submitting something empty that would fail.
    if (!pending) return;

    submitAction({ button: { id: resumeId },
                   answers: pending.answers, payload: pending.payload });
  }, [signedIn, actionButtons, submitAction]);

  return {
    signedIn, setSignedIn,
    actionState, setActionState,
    completedKeys,
    submitAction,
    runAction,
    runActionForEach,
    resumeAfterSignIn,
  };
}
